import type { Ref } from 'vue'

export enum ExperimentName {
    LANDING_PAGE = 'landingPage',
    SLOTS_LANDING_PAGE = 'slotsLandingPage',
    CASINO_LANDING_PAGE = 'casinoLandingPage',
    BINGO_LANDING_PAGE = 'bingoLandingPage',
    SIGNUP = 'signup',
    SIGNUP_PROMOTION = 'signupPromotion',
    PAYMENT = 'payment',
    HERO = 'hero',
    ACCOUNT_COMPLETION = 'accountCompletion',
    PASSWORD_STRENGTH = 'passwordStrength',
    RECURRING_CONSENT = 'recurringConsent',
    CURATED_GAMES_PAGE = 'curatedGamesPage',
    WHEEL_LANDING_PAGE = 'wheelLandingPage',
    GAME_ASSET_AB = 'gameAssetAb',
    GAME_SEARCH = 'gameSearch',
}

export type ExperimentNameUnion = `${ExperimentName}`

export enum ExperimentCookieName {
    LANDING_PAGE = 'experiment_landingpage_test',
    SLOTS_LANDING_PAGE = 'experiment_slots_landingpage_test',
    CASINO_LANDING_PAGE = 'experiment_casino_landingpage_test',
    BINGO_LANDING_PAGE = 'experiment_bingo_landingpage_test',
    SIGNUP = 'experiment_signup_test',
    SIGNUP_PROMOTION = 'experiment_signup_promotion_test',
    PAYMENT = 'experiment_payment_test',
    HERO = 'experiment_hero_test',
    ACCOUNT_COMPLETION = 'experiment_account_completion',
    PASSWORD_STRENGTH = 'experiment_password_strength',
    RECURRING_CONSENT = 'experiment_recurring_consent',
    CURATED_GAMES_PAGE = 'experiment_curated_games_page_test',
    WHEEL_LANDING_PAGE = 'experiment_wheel_landingpage_test',
    PERSONALIZED_LOBBY_GAMES = 'experiment_personalized_lobby_games_test',
    DEPOSIT_FIRST_HERO = 'experiment_deposit_first_hero',
    GAME_ASSET_AB = 'experiment_game_asset_ab',
    GAME_SEARCH = 'experiment_game_search_test',
}

export type ExperimentCookieNameUnion = `${ExperimentCookieName}`

export enum ExperimentValue {
    A = 'a',
    B = 'b',
    C = 'c',
    D = 'd',
    E = 'e',
}

export type ExperimentValueUnion = `${ExperimentValue}`

/**
 * Experiment type that defines the type we're experimenting
 */
export enum ExperimentType {
    LANDING_PAGE = 1,
    SLOTS_LANDING_PAGE = 2,
    CASINO_LANDING_PAGE = 3,
    BINGO_LANDING_PAGE = 4,
    SIGNUP = 5,
    SIGNUP_PROMOTION = 6,
    PAYMENT = 7,
    PASSWORD_STRENGTH = 8,
    HERO = 9,
    ACCOUNT_COMPLETION = 10,
    RECURRING_CONSENT = 11,
    CURATED_GAMES_PAGE = 12,
    WHEEL_LANDING_PAGE = 13,
    GAME_ASSET_AB = 15,
    GAME_SEARCH = 16,
}

export type ExperimentTypeUnion = `${ExperimentType}`

/** A and B make a test; a concluded test keeps only its winner. */
export type ExperimentVersions = Partial<Record<ExperimentValueUnion, string>>

export type UseExperimentReturn = Ref<ExperimentValueUnion>

export type UseExperimentReturnReadonly = Readonly<UseExperimentReturn>

export interface CoreExperiment {
    name: ExperimentNameUnion
    cookieName: ExperimentCookieNameUnion
    versions: ExperimentVersions
    /**
     * @description
     * Sets the cookie after the user has been authenticated
     */
    needsAuthentication?: boolean
    /**
     * @description
     * Assignment method for the experiment.
     * - 'random': Random assignment (default)
     * - 'userId': Deterministic assignment based on user ID
     */
    assignmentMethod?: 'random' | 'userId'
    /**
     * @description
     * Group size for userId assignment method.
     * - 1: alternating (0,1,0,1,0,1)
     * - 2: pairs (0,0,1,1,0,0,1,1)
     * - 3: triplets (0,0,0,1,1,1,0,0,0)
     * @default 1
     */
    groupSize?: number
}

export interface ExperimentOptions {
    cookieName: ExperimentCookieNameUnion
    /**
     * @description
     * ExperimentName is optional when the allowWrite property is false
     */
    experimentName: ExperimentNameUnion
    /**
     * @description
     * Not all experiments should be writeable, ex when the useExperiment is used within a component or another composable, so we only allow the experiment composable to write, if this option is set to "true"
     */
    allowWrite?: boolean
    /**
     * @description
     * Write mode only: the arm `?experiment=<name>:<arm>` forces (see `experimentOverride`). Ignored unless it is a configured arm.
     */
    override?: string
}

/** The arm and version a visitor is counted in by tracking. */
export interface ExperimentAssignment {
    variant: ExperimentValueUnion
    version: string
}
